import { marshyMeadows } from "./marshyMeadows";

const B = (th, y, r, t, color) => ({ th, y, r, t, color });

// "Existence": a finished painter's table → paint-water grey wash and a knife scrape → blooming circles → the painting.
export const existence = {
  slug: "existence",
  duration: 29,
  stages: [
    { id: "table", at: 0, hint: "after the painting" },
    { id: "wash", at: 5.5, hint: "the remnants" },
    { id: "bloom", at: 13, hint: "tap to bloom a circle" },
    { id: "settle", at: 21.5, hint: "" },
  ],
  line: { text: "Even the remnants of who we were can bloom into something beautiful.", from: 14, to: 28, pos: "low" },
  figures: {
    list: [{ src: "/arcs/existence/table.webp", flat: true, y: 0.92, z: -1.5, w: 2.5, h: 1.68, th: 0 }],
    appear: [0, 1.2],
    thread: [99, 100],
    fade: [7, 11],
  },
  figFrag: marshyMeadows.figFrag,
  table: { w: 2.9, h: 2.0, y: 0.88, z: -1.5 },
  cues: [[8.2, "scrape"], [10.4, "scrape"]],
  blooms: [
    B(0, 2.4, 1.1, 13.2, "#3F6FC8"), B(-0.7, 3.4, 0.8, 14.4, "#8A4FB8"), B(0.75, 1.7, 0.9, 15.4, "#F08A2B"),
    B(-1.6, 1.9, 1.0, 16.4, "#F2C53A"), B(1.7, 3.0, 1.0, 17.2, "#3F6FC8"), B(2.6, 2.2, 0.85, 18, "#8A4FB8"), B(-2.6, 2.8, 0.9, 18.8, "#F08A2B"),
    B(0.35, 3.3, 0.7, 13.8, "#F2C53A"), B(-0.3, 1.5, 0.75, 14.9, "#8A4FB8"), B(1.15, 2.6, 0.8, 15.9, "#3F6FC8"), B(-1.1, 2.7, 0.85, 16.9, "#F08A2B"),
    B(2.15, 1.6, 0.7, 17.6, "#F2C53A"), B(-2.1, 3.6, 0.75, 18.4, "#3F6FC8"), B(3.0, 3.2, 0.8, 19.0, "#F08A2B"), B(-3.0, 1.7, 0.7, 19.5, "#8A4FB8"),
  ],
  touch: { kind: "bloom", from: 13, until: 21.5, colors: ["#3F6FC8", "#8A4FB8", "#F08A2B", "#F2C53A"] },
  ambient: null,
  look: { start: -0.55, lift: [6, 10], end: 0.05, yaw: [[0, 0], [12, 0], [16, 0.35], [20, -0.35], [24, 0], [29, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "table", pos: "low" },
  speed: [[0, 0.1], [29, 0.1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uBloom[20];
uniform vec3 uBloomCol[20];
varying vec3 vPos;
${"__NOISE__"}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
void main(){
  float n = snoise(vec3(vPos * 0.7)) * 0.5 + 0.5;
  float n2 = snoise(vec3(vPos * 3.1 + 4.0)) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float settle = smoothstep(21.5, 26.5, uT);
  vec3 quiet = vec3(0.93, 0.92, 0.9);
  vec3 col;
  vec2 local;
  float dist;
  if (uSurface < 0.5) {
    col = mix(quiet, vec3(0.86, 0.85, 0.84), smoothstep(0.0, 9.0, vPos.y));
    local = vec2(th * uRadius, vPos.y - uCy);
    dist = 2.2 + abs(th) * uRadius * 0.55 + abs(vPos.y - 1.0) * 0.5;
  } else if (uSurface < 1.5) {
    col = mix(vec3(0.62, 0.55, 0.48), vec3(0.7, 0.63, 0.55), n);
    local = vPos.xz;
    dist = length(vPos.xz - vec2(0.0, -1.5));
  } else {
    col = quiet * 1.02;
    local = vPos.xz;
    dist = 9.0 + (uRadius - length(vPos.xz)) * 0.5;
  }
  // the leftover waters: a soft grey wash with granulation, spreading out from the table
  float reach = (uT - 5.8) * 1.7;
  float wash = 1.0 - smoothstep(reach - 1.6, reach, dist + n * 1.4);
  vec3 grey = mix(vec3(0.58, 0.58, 0.6), vec3(0.7, 0.69, 0.68), n) * (0.92 + 0.12 * n2);
  grey = mix(grey, vec3(0.52, 0.5, 0.55), smoothstep(0.7, 0.9, n2) * 0.4);
  col = mix(col, grey, wash * step(0.0, uT - 5.5));
  // palette-knife scrapes dragged across the front wall, leaving a pale worn surface
  if (uSurface < 0.5) {
    for (int i = 0; i < 2; i++){
      float fi = float(i);
      float t0 = 8.2 + fi * 2.2;
      float k = smoothstep(t0, t0 + 1.1, uT);
      float x = th * uRadius + 2.6;
      float y0 = 2.9 - fi * 1.3 + 0.12 * x;
      float band = (1.0 - smoothstep(0.16, 0.24, abs(vPos.y - y0 + 0.05 * sin(x * 2.0))));
      float drawn = step(x, k * 5.4) * step(0.0, x);
      float streak = 0.5 + 0.5 * sin((vPos.y - y0) * 140.0 + n2 * 4.0);
      col = mix(col, vec3(0.88, 0.87, 0.85) - 0.12 * streak, band * drawn * 0.85);
    }
  }
  // blooms: large painted shapes that drift, overlap and merge (a soft field), rims pooled darker, glowing within
  if (uSurface < 0.5) {
    float field = 0.0;
    vec3 acc = vec3(0.0);
    float glow = 0.0;
    for (int i = 0; i < 20; i++){
      vec4 b = uBloom[i];
      if (b.w <= 0.0 || uT < b.w) continue;
      float fi = float(i);
      float g = smoothstep(0.0, 2.4, uT - b.w);
      vec2 c = vec2(b.x + 0.07 * sin(uT * 0.35 + fi * 1.3), b.y + 0.22 * sin(uT * 0.27 + fi * 2.1) + 0.04 * (uT - b.w));
      float r = b.z * (0.2 + 0.8 * g) * (1.0 + 0.06 * sin(uT * 0.9 + fi));
      vec2 dv = vec2((th - c.x) * uRadius, vPos.y - c.y);
      float d2 = dot(dv, dv) + 0.0004;
      float w = r * r / d2;
      field += w;
      acc += uBloomCol[i] * w;
      glow += exp(-d2 / (r * r * 0.25)) * g;
    }
    float wob = snoise(vPos * 1.6 + vec3(0.0, 0.0, uT * 0.05)) * 0.12 + n2 * 0.06;
    float f = field + wob;
    if (field > 0.0) {
      vec3 c = acc / field;
      float inside = smoothstep(0.92, 1.05, f);
      float rim = inside * (1.0 - smoothstep(1.05, 1.6, f));
      vec3 bloom = mix(c * 1.25 + 0.12, c * 0.68, rim);
      bloom += vec3(1.0, 0.98, 0.92) * min(glow, 1.0) * 0.32;
      col = mix(col, bloom, inside * 0.9);
      col += c * 0.14 * smoothstep(0.35, 0.92, f) * (1.0 - inside);
    }
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float m = smoothstep(0.0, 0.25, settle * 1.3 - n * 0.3 - (uSurface < 0.5 ? (10.0 - vPos.y) / 20.0 : 0.25));
  col = mix(col, paint, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
